import React, { VFC, useEffect, useRef, CSSProperties } from 'react'
import lottiePlayer, {
  AnimationItem,
  AnimationConfigWithData,
  AnimationEventName,
  AnimationEventCallback
} from 'lottie-web/build/player/lottie_light'

export interface ReactLottieEvent<T = unknown> {
  name: AnimationEventName
  callback: AnimationEventCallback<T>
}

export type LottieOptions = Omit<AnimationConfigWithData, 'container'> &
  Partial<Pick<AnimationConfigWithData, 'container'>>

export interface LottieProps {
  options: LottieOptions
  eventListeners?: ReactLottieEvent[]
  isPaused?: boolean
  isStopped?: boolean
  className?: string
  style?: CSSProperties
}

export const Lottie: VFC<LottieProps> = ({
  options,
  eventListeners = [],
  isPaused = false,
  isStopped = false,
  className,
  style
}) => {
  const { container } = options
  const lottieAnimationRef = useRef<AnimationItem | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const currContainer = container || containerRef.current
    if (!currContainer) return

    lottieAnimationRef.current = lottiePlayer.loadAnimation({ ...options, container: currContainer })
    addEventListeners(eventListeners)
    return () => {
      lottieAnimationRef.current?.destroy()
      removedEventListeners(eventListeners)
    }
  }, [container, eventListeners, options])

  useEffect(() => {
    if (!lottieAnimationRef.current) return

    if (isPaused) {
      lottieAnimationRef.current.pause()
    } else {
      lottieAnimationRef.current.play()
    }
  }, [isPaused])

  useEffect(() => {
    if (!lottieAnimationRef.current) return

    if (isStopped) {
      lottieAnimationRef.current.stop()
    } else {
      lottieAnimationRef.current.play()
    }
  }, [isStopped])

  const addEventListeners = (events: ReactLottieEvent[]) => {
    if (!lottieAnimationRef.current) return

    for (const event of events) {
      lottieAnimationRef.current.addEventListener(event.name, event.callback)
    }
  }

  const removedEventListeners = (events: ReactLottieEvent[]) => {
    if (!lottieAnimationRef.current) {
      return
    }

    for (const event of events) {
      lottieAnimationRef.current.removeEventListener(event.name, event.callback)
    }
  }

  return <>{!container && <div className={className} style={style} ref={containerRef} />}</>
}
